import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import SOURCE_TYPE from '../SourceCreate.constants';
import {
  selectedSourceDetailsAtom,
  selectedSourceTypeIdAtom,
  sourceDialogModeAtom
} from '../SourceCreate.store';
import { SourceDialogMode } from '../SourceCreate.types';
import type {
  DataProviderRowState,
  UseDataProviderRowStateOptions
} from './DataProvider.types';
import { getSavedMicrosoftProviderNames } from './DataProvider.utils';

/**
 * Custom hook that manages UI state for a data provider row.
 *
 * **Responsibilities:**
 * 1. Manages row expansion/collapse state
 * 2. Auto-expands when provider is selected
 * 3. Tracks initial selection state for later comparison
 * 4. Determines if provider checkbox should be disabled (Edit mode Microsoft)
 * 5. Determines if checkbox should be shown (hides for MicrosoftEntraID parent)
 *
 * **Business Rules:**
 * - Microsoft Edit mode: Disable checkbox for providers already saved in database
 * - Microsoft Entra ID: Never show checkbox (parent-level selection only)
 * - Auto-expand: When provider is selected, expand to show fields
 *
 * @param options - Configuration for row state management
 * @returns Object containing all row state and controls
 *
 * @example
 * const {
 *   open,
 *   setOpen,
 *   isSelected,
 *   isProviderDisabled,
 *   shouldShowCheckbox,
 *   mode
 * } = useDataProviderRowState({
 *   parentName: 'data_provider.O365',
 *   dataSourceName: 'O365'
 * });
 */
export const useDataProviderRowState = (
  options: UseDataProviderRowStateOptions
): DataProviderRowState => {
  const { parentName, dataSourceName } = options;

  const { control } = useFormContext();
  const [open, setOpen] = useState(true);
  const [selectedSourceDetails] = useAtom(selectedSourceDetailsAtom);
  const [mode] = useAtom(sourceDialogModeAtom);
  const [selectedSourceTypeId] = useAtom(selectedSourceTypeIdAtom);
  const initialSelectionRef = useRef<boolean | null>(null);

  const checkboxName = `${parentName}.selected`;
  const isSelected = useWatch({ name: checkboxName, control }) || false;

  // Disable checkbox if provider already exists in Edit mode for Microsoft sources
  const isProviderDisabled =
    mode === SourceDialogMode.Edit &&
    selectedSourceTypeId === SOURCE_TYPE.MICROSOFT &&
    getSavedMicrosoftProviderNames(selectedSourceDetails).includes(
      dataSourceName
    );

  // Don't show checkbox for MicrosoftEntraID or disabled providers in Edit mode
  const shouldShowCheckbox =
    dataSourceName !== 'MicrosoftEntraID' &&
    !(mode === SourceDialogMode.Edit && isProviderDisabled);

  // Capture initial selection state on mount
  useEffect(() => {
    initialSelectionRef.current = isSelected;
  }, []);

  // Auto-expand when selection changes to selected (user may collapse afterwards)
  const [prevIsSelected, setPrevIsSelected] = useState(isSelected);
  if (prevIsSelected !== isSelected) {
    setPrevIsSelected(isSelected);
    if (isSelected) {
      setOpen(true);
    }
  }

  return {
    open,
    setOpen,
    isSelected,
    isProviderDisabled,
    shouldShowCheckbox,
    mode,
    initialSelectionRef
  };
};
